import { Cell, Content, Divider, Flex, Text, View } from '@adobe/react-spectrum';
import { getGrades } from '../../../data/ScoreDisplayUtils';
import { SORT_MAP } from '../../../data/redux/slices/leaderboardSlice';
import { formatRelativeTime } from '../../../data/relativeTime';
import { capBracketRating, capDisplayValueForSort, capPowerLevel } from '../../../data/displayCaps';
import store from '../../../data/redux/store/AppStore';
import React from 'react';
import './ResultsCellRenderers.css';
import { AvatarImage } from '../../avatarImage/AvatarImage';
import { AuthorAvatar } from '../../avatarImage/AuthorAvatar';

const formatMetric = (value, decimals = 1) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n.toFixed(decimals);
};

const formatSaltMetric = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n >= 100 ? Math.round(n).toString() : n.toFixed(1);
};

const buildMetricStrip = (item, activeSortEnum, isNarrow) => {
    const chips = [];

    if (activeSortEnum !== SORT_MAP.POWERLEVEL.enum) {
        const v = formatMetric(capPowerLevel(item?.powerLevelRating), 1);
        if (v) chips.push({ key: 'power', label: 'PWR', value: v });
    }
    if (activeSortEnum !== SORT_MAP.BRACKET.enum) {
        const v = formatMetric(capBracketRating(item?.bracketRating), 1);
        if (v) chips.push({ key: 'bracket', label: isNarrow ? 'BRKT' : 'BRACKET', value: v });
    }
    if (activeSortEnum !== SORT_MAP.SALT.enum) {
        const v = formatSaltMetric(item?.saltRating);
        if (v) chips.push({ key: 'salt', label: 'SALT', value: v });
    }
    if (activeSortEnum !== SORT_MAP.INGESTDATE.enum) {
        const v = formatRelativeTime(item?.ingestDate, { compact: true });
        if (v) chips.push({ key: 'ingest', label: '', value: v });
    }
    if (activeSortEnum !== SORT_MAP.LIKECOUNT.enum) {
        const n = Number(item?.likeCount);
        if (Number.isFinite(n) && n > 0) {
            chips.push({ key: 'likes', label: '♥', value: String(n) });
        }
    }

    return isNarrow ? chips.slice(0, 3) : chips;
};
// import { Card, CardBody, CardPreview } from '@adobe/react-spectrum';

export const resultsCellRenderers = {
    CARD_LIST: (item, columnKey, clickCallBack) => {
        if (!store || !store?.getState()) {
            return <div />;
        }

        switch (columnKey) {
            case 'names':
            case 'name':
                let i = -1;

                const handleClick = (index) => {
                    clickCallBack && clickCallBack(item?.imageUris?.[index] || null);
                };

                return (
                    <div>
                        <Content>
                            <Flex direction={'column'}>
                                {item?.names &&
                                    item.names.map((name) => {
                                        const index = ++i;

                                        return (
                                            <span
                                                onClick={() => handleClick(index)}
                                                className={
                                                    item?.imageUris
                                                        ? 'commanderTiersTileStatisticsGridCellContentLeftClickable'
                                                        : 'commanderTiersTileStatisticsGridCellContentLeft'
                                                }
                                            >
                                                {name}
                                            </span>
                                        );
                                    })}
                            </Flex>
                        </Content>
                    </div>
                );
            default:
                if (columnKey === 'row_footer') return null;
                return (
                    <div>
                        <div className={'commanderTiersTileStatisticsGridCellContentLeft'}>{item[columnKey]}</div>
                    </div>
                );
        }
    },
    UPGRADES: (item, columnKey) => {
        if (!store || !store?.getState()) {
            return <div />;
        }

        switch (columnKey) {
            case 'name':
                return (
                    <div>
                        <Content height={450}>
                            <Flex
                                direction={'column'}
                                justifyContent={'center'}
                                justifySelf={'center'}
                                alignItems={'center'}
                                maxWidth={500}
                                margin={30}
                                height={450}
                            >
                                <span className={'nameAndScoreLabel'} style={{ textAlign: 'center' }}>
                                    {item.name?.toUpperCase()}
                                </span>
                                <img src={item.imageUri} width={300} />
                                <span className={'nameAndScoreLabel'} style={{ textAlign: 'center' }}>
                                    SCORE: {item.score}
                                </span>
                            </Flex>
                        </Content>
                    </div>
                );
            default:
                if (columnKey === 'row_footer') return null;
                return (
                    <div>
                        <div className="TextCell">{item[columnKey]}</div>
                    </div>
                );
        }
    },
    LEADERBOARD: (item, columnKey, options = {}) => {
        if (!store || !store?.getState()) {
            return <div />;
        }

        const compact = Boolean(options?.compact);
        const rowHeight = compact ? 80 : 90;
        const avatarSize = compact ? 50 : 50;

        let __subscriberTier = parseInt(item?.perks?.tier) ? `L${item?.perks?.tier}` : 'MAX';

        const sortBy = store.getState()?.[item?.storeKey]?.sortBy || store.getState()?.leaderboard?.sortBy;

        switch (columnKey) {
            case 'authorAvatarUrl':
                const avatarUrl = item?.author?.profileImageUrl;
                const colors = item?.colorIdentity?.match(/./g) || [];

                return (
                    <div>
                        <Content marginTop={compact ? 6 : 7} marginBottom={compact ? 2 : 7}>
                            <Flex
                                direction={'column'}
                                // height={90}
                                justifyContent={'center'}
                                alignItems={'center'}
                                alignContent={'center'}
                                marginTop={0}
                            >
                                <div className="cellAvatar">
                                    {item?.perks?.avatarUri && item?.perks?.avatarUri !== '' ? (
                                        <AvatarImage size={avatarSize} avatarUrl={item.perks.avatarUri} />
                                    ) : (
                                        <AuthorAvatar
                                            avatarUrl={avatarUrl}
                                            width={`${avatarSize}px`}
                                            height={`${avatarSize}px`}
                                            alt={item.author?.username}
                                            style={{ objectFit: 'cover', borderRadius: 2 }}
                                        />
                                    )}
                                </div>
                                {!compact && (
                                    <div className="cellAvatarManaSymbols">
                                        {colors && colors.length > 0 && (
                                            <div>
                                                {colors?.map((symbol) => {
                                                    return (
                                                        <span
                                                            key={`${item?.id}__color${symbol?.toLowerCase()}`}
                                                            className={`mana xs s${symbol?.toLowerCase()}`}
                                                            style={{ opacity: '1' }}
                                                        ></span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {item?.perks?.showSubscriberLevelOnDeckRows && item?.perks?.tier !== 'none' && (
                                    <Flex
                                        direction={'column'}
                                        justifyContent={'center'}
                                        alignItems={'center'}
                                        alignContent={'center'}
                                    >
                                        <span className={'subscriberLevelLabel'} style={{ fontSize: '.8rem' }}>
                                            {__subscriberTier}
                                        </span>
                                        <span className={'subscriberLevelLabel'}>SUPPORTER</span>
                                    </Flex>
                                )}
                            </Flex>
                        </Content>
                    </div>
                );
                break;
            case 'saltRating':
                const isNarrow =
                    compact || (typeof window !== 'undefined' && window.innerWidth && window.innerWidth < 700);

                if (sortBy?.enum === SORT_MAP.INGESTDATE.enum) {
                    const ts = item?.displayValue ?? item?.[SORT_MAP.INGESTDATE.field];
                    const relative =
                        formatRelativeTime(ts, { compact: isNarrow }) || (isNarrow ? '>2y ago' : '>2 years ago');
                    return (
                        <span className="resultsCellScoreWrapper">
                            <span className="resultsCellRelativeTime">{relative}</span>
                        </span>
                    );
                }

                const score = capDisplayValueForSort(item?.displayValue || 0, sortBy?.enum);
                const grade = getGrades(item);
                const prop = sortBy?.enum.toLowerCase();
                let floatValue = parseFloat(/[0-9]*\.[0-9]/.exec(score.toString())?.[0] || `${score}`);
                if (!floatValue.toString().includes(`.`)) {
                    floatValue = `${floatValue}.0`;
                }

                return (
                    <span className="resultsCellScoreWrapper">
                        <span
                            className={`resultsCellScore${isNarrow ? ' resultsCellScore--compact' : ''}`}
                            style={{ color: grade?.[prop]?.color }}
                        >
                            {floatValue}
                        </span>
                    </span>
                );
                break;
            case 'commanders':
                let commanderList = item?.commanders;
                if (!Array.isArray(commanderList)) {
                    commanderList = [item?.commanders];
                }
                // Join partner commanders with " + " — matches the convention
                // used elsewhere (CommanderDetails one-line title) and stays
                // legible when one commander has its own comma in the name
                // (e.g. "Tevesh Szat, Doom of Fools + Thrasios, Triton Hero").
                const commanders = commanderList.filter(Boolean).join(' + ');

                const authorName = item?.author?.username;

                return (
                    <div style={{ minWidth: 0, width: '100%' }}>
                        <Content minHeight={rowHeight}>
                            <Flex
                                direction={'column'}
                                minWidth={0}
                                justifyContent={'start'}
                                UNSAFE_style={{ paddingTop: 6, paddingBottom: 6 }}
                            >
                                <Text
                                    data-role={'small-header'}
                                    UNSAFE_className={compact ? 'deckTitleLineClampCompact' : 'deckTitleLineClamp'}
                                    UNSAFE_style={{ color: '#fcef95' }}
                                >
                                    {item?.title}
                                </Text>
                                <Divider
                                    size="S"
                                    width="100%"
                                    marginTop={compact ? 2 : 5}
                                    marginBottom={compact ? 2 : 5}
                                    UNSAFE_style={{ backgroundColor: '#252525' }}
                                />
                                <Text UNSAFE_className={compact ? 'deckTitleLineClampCompact' : undefined}>
                                    {commanders}
                                </Text>
                                {authorName && <Text UNSAFE_className="resultsRowAuthor">by {authorName}</Text>}
                            </Flex>
                        </Content>
                    </div>
                );

                break;
            case 'row_footer':
                const isNarrowFooter =
                    compact || (typeof window !== 'undefined' && window.innerWidth && window.innerWidth < 700);
                const footerChips = buildMetricStrip(item, sortBy?.enum, isNarrowFooter);
                if (footerChips.length === 0) return null;
                return (
                    <div className="resultsRowMetricStrip">
                        {footerChips.map((chip) => (
                            <span key={chip.key} className={`resultsRowMetric resultsRowMetric--${chip.key}`}>
                                {chip.label && <span className="resultsRowMetricLabel">{chip.label}</span>}
                                <span className="resultsRowMetricValue">{chip.value}</span>
                            </span>
                        ))}
                    </div>
                );
            default:
                return (
                    <div>
                        <Content height={rowHeight}>
                            <Text>{item[columnKey]}</Text>
                        </Content>
                    </div>
                );
        }
    },
};
